import { PageHeader } from "@/components/PageHeader";
import { SortableHeader } from "@/components/SortableHeader";
import { api, type PaginatedResponse } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import type { UserSummaryItem } from "@/types";
import type { LifecyclePeriodId } from "@a7y/types";
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@a7y/tailwind-catalyst";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Search, Users as UsersIcon, X } from "lucide-react";
import { type ChangeEvent, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Shared constants for lifecycle display
// ---------------------------------------------------------------------------

const PERIOD_LABELS: Record<string, string> = {
  "p0-c": "Trial Onboarding",
  "p0-d": "Trial User",
  "p0-lapsed": "Lapsed",
  "p1": "New Subscriber",
  "p2-p6": "Establishing",
  "p6+": "Retained",
  "p1-cancelled": "Cancelled",
  "p2-p6-cancelled": "Cancelled",
  "p6+-cancelled": "Cancelled",
};

const PERIOD_BADGE_COLORS: Record<string, "zinc" | "amber" | "blue" | "indigo" | "green" | "red"> = {
  "p0-c": "zinc",
  "p0-d": "zinc",
  "p0-lapsed": "amber",
  "p1": "blue",
  "p2-p6": "indigo",
  "p6+": "green",
  "p1-cancelled": "red",
  "p2-p6-cancelled": "red",
  "p6+-cancelled": "red",
};

const PERIOD_FILTER_OPTIONS: { value: LifecyclePeriodId; label: string }[] = [
  { value: "p0-c", label: "Trial Onboarding (p0-c)" },
  { value: "p0-d", label: "Trial User (p0-d)" },
  { value: "p0-lapsed", label: "Lapsed (p0-lapsed)" },
  { value: "p1", label: "New Subscriber (p1)" },
  { value: "p2-p6", label: "Establishing (p2-p6)" },
  { value: "p6+", label: "Retained (p6+)" },
  { value: "p1-cancelled", label: "Cancelled (p1-cancelled)" },
  { value: "p2-p6-cancelled", label: "Cancelled (p2-p6-cancelled)" },
  { value: "p6+-cancelled", label: "Cancelled (p6+-cancelled)" },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SortField = "name" | "email" | "membershipCount" | "createdAt";
type SortOrder = "asc" | "desc";

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function UsersPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [periodFilter, setPeriodFilter] = useState<LifecyclePeriodId | "">("");
  const [includeApptonomy, setIncludeApptonomy] = useState(false);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 400);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder(field === "createdAt" ? "desc" : "asc");
    }
    setPage(1);
  };

  const pageSize = 30;

  const { data, isLoading } = useQuery<PaginatedResponse<UserSummaryItem>>({
    queryKey: ["users", { page, search: debouncedSearch, sortField, sortOrder, periodFilter, includeApptonomy }],
    queryFn: () =>
      api.getUsers({
        page,
        limit: pageSize,
        search: debouncedSearch || undefined,
        sortBy: sortField,
        sortOrder,
        periodId: periodFilter || undefined,
        includeApptonomy,
      }),
  });

  const users = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="px-4 py-0">
      <PageHeader title="Users" />

      {/* Filter bar */}
      <div className="mb-6 flex items-center gap-3">
        <div className="relative w-[340px]">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            autoComplete="off"
            placeholder="Search by user ID, email, or name..."
            value={search}
            onChange={handleSearchChange}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-10 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white"
          />
          {search && (
            <button
              onClick={() => {
                setSearch("");
                setDebouncedSearch("");
                setPage(1);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <select
          value={periodFilter}
          onChange={(e) => {
            setPeriodFilter(e.target.value as LifecyclePeriodId | "");
            setPage(1);
          }}
          className="rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white"
        >
          <option value="">All Periods</option>
          {PERIOD_FILTER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
          <input
            type="checkbox"
            checked={includeApptonomy}
            onChange={(e) => {
              setIncludeApptonomy(e.target.checked);
              setPage(1);
            }}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-zinc-600 dark:bg-zinc-800"
          />
          Include @apptonomy.ai
        </label>
      </div>

      {/* Users Table */}
      <Table dense striped>
        <TableHead>
          <TableRow>
            <SortableHeader field="name" label="User" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="email" label="Email" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="membershipCount" label="Org Count" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <SortableHeader field="createdAt" label="Created" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} />
            <TableHeader>Period</TableHeader>
            <TableHeader className="text-right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center">
                <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
              </TableCell>
            </TableRow>
          ) : users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="py-8 text-center">
                <UsersIcon className="mx-auto h-12 w-12 text-zinc-400" />
                <p className="mt-2 text-zinc-500">
                  {debouncedSearch
                    ? "No users found matching your search"
                    : "No users found"}
                </p>
              </TableCell>
            </TableRow>
          ) : (
            users.map((user) => (
              <TableRow key={user.id} href={`/admin/users/${user.id}`}>
                <TableCell className="font-medium">
                  <div>
                    {[user.firstName, user.lastName]
                      .filter(Boolean)
                      .join(" ") || "\u2014"}
                    <div className="font-mono text-xs text-zinc-500">
                      {user.id}
                    </div>
                  </div>
                </TableCell>
                <TableCell>{user.email || "\u2014"}</TableCell>
                <TableCell>
                  <Badge color={user.membershipCount > 0 ? "blue" : "zinc"}>
                    {user.membershipCount} org
                    {user.membershipCount !== 1 ? "s" : ""}
                  </Badge>
                </TableCell>
                <TableCell className="text-zinc-500">
                  {user.createdAt ? formatDate(user.createdAt) : "\u2014"}
                </TableCell>
                <TableCell>
                  {user.periodId ? (
                    <Badge color={PERIOD_BADGE_COLORS[user.periodId] ?? "zinc"}>
                      {PERIOD_LABELS[user.periodId] ?? user.periodId}
                    </Badge>
                  ) : (
                    <span className="text-zinc-400">{"\u2014"}</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <ChevronRight className="inline h-5 w-5 text-zinc-400" />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {total > pageSize && (
        <div className="mt-6 flex items-center justify-between">
          <Text className="text-sm text-gray-700 dark:text-zinc-300">
            Showing{" "}
            <span className="font-medium">{(page - 1) * pageSize + 1}</span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(page * pageSize, total)}
            </span>{" "}
            of <span className="font-medium">{total}</span> results
          </Text>
          <div className="flex gap-2">
            <Button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              outline
            >
              Previous
            </Button>
            <Button
              onClick={() => setPage(page + 1)}
              disabled={page * pageSize >= total}
              outline
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
